import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, GitCompare, ListChecks, Plus, Trash2, X } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import type { Program } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { Alert, Badge } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { AiButton } from './Assistant';

interface Finding {
  key: number;
  area: string;
  item: string | null;
  finding: string;
  location: string | null;
}
interface Review {
  runId: string;
  version: number;
  coverage: { parts: number; total: number };
  summary: string;
  findings: Finding[];
}
interface Comparison {
  runId: string;
  from: number;
  to: number;
  summary: string;
  changes: string[];
  openPoints: string[];
}

const findingBody = (f: Finding) =>
  `**${f.area}${f.item ? `: ${f.item}` : ''}**\n\n${f.finding}${f.location ? `\n\nWhere: ${f.location}` : ''}`;

const comparisonBody = (c: Comparison) =>
  [
    `**Changes from v${c.from} to v${c.to}**`,
    c.summary,
    ...(c.changes.length ? ['', ...c.changes.map((x) => `- ${x}`)] : []),
    ...(c.openPoints.length ? ['', '**Open points**', ...c.openPoints.map((x) => `- ${x}`)] : []),
  ].join('\n');

/**
 * BlackWolf's document actions on a deliverable: review the latest draft against its checklist,
 * and compare two versions. Proposals only; each is posted to the comment thread on acceptance.
 */
export function DeliverableAi({
  program,
  deliverableId,
  versions,
  hasChecklist,
  onPosted,
}: {
  program: Program;
  deliverableId: string;
  versions: number[];
  hasChecklist: boolean;
  onPosted: () => void;
}) {
  const toast = useToast();
  const base = `/api/programs/${program.id}/deliverables/${deliverableId}/ai`;
  const latest = Math.max(0, ...versions);
  const [review, setReview] = useState<Review | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [done, setDone] = useState<Record<number, 'posted' | 'dismissed'>>({});
  const [comparing, setComparing] = useState(false);
  const [from, setFrom] = useState(Math.max(1, latest - 1));
  const [to, setTo] = useState(latest);
  const [comparison, setComparison] = useState<Comparison | null>(null);
  const [summaryPosted, setSummaryPosted] = useState(false);

  const run = useMutation({
    mutationFn: () => api.post<Review>(`${base}/review`, {}),
    onSuccess: (r) => {
      setReview(r);
      setDrafts(Object.fromEntries(r.findings.map((f) => [f.key, findingBody(f)])));
      setDone({});
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const compare = useMutation({
    mutationFn: () => api.post<Comparison>(`${base}/compare`, { from, to }),
    onSuccess: (c) => {
      setComparison(c);
      setSummaryPosted(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const accept = useMutation({
    mutationFn: (v: { runId: string; body: string; key?: number }) =>
      api.post(`${base}/accept`, { runId: v.runId, body: v.body }),
    onSuccess: (_r, v) => {
      if (v.key !== undefined) setDone((d) => ({ ...d, [v.key!]: 'posted' }));
      else setSummaryPosted(true);
      onPosted();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <AiButton
          program={program}
          tier={2}
          onClick={() => run.mutate()}
          loading={run.isPending}
          disabled={!latest || !hasChecklist}
        >
          <ListChecks /> Review the draft
        </AiButton>
        <AiButton
          program={program}
          tier={2}
          onClick={() => {
            setComparison(null);
            setFrom(Math.max(1, latest - 1));
            setTo(latest);
            setComparing(true);
          }}
          disabled={versions.length < 2}
        >
          <GitCompare /> Compare versions
        </AiButton>
      </div>
      {latest > 0 && !hasChecklist && (
        <p className="mt-1 text-xs text-muted">Add review checklist items to review the draft.</p>
      )}

      <Dialog
        open={!!review}
        onClose={() => setReview(null)}
        wide
        title={review ? `Review of v${review.version}` : ''}
        description="Proposals only. Each finding you post goes to the comment thread with an AI-assisted badge."
        footer={
          <Button variant="secondary" onClick={() => setReview(null)}>
            Close
          </Button>
        }
      >
        {review && (
          <div className="space-y-3 text-sm">
            <p>{review.summary}</p>
            {review.coverage.parts < review.coverage.total && (
              <Alert tone="warn" title="Long document">
                The review covered the first {review.coverage.parts} of {review.coverage.total} parts.
              </Alert>
            )}
            {!review.findings.length && <p className="text-muted">No findings.</p>}
            {review.findings.map((f) => (
              <div key={f.key} className="rounded-md border border-border p-3">
                <p className="mb-1 text-xs font-medium text-muted">
                  {f.area}
                  {f.item ? `: ${f.item}` : ''}
                </p>
                {done[f.key] ? (
                  <p className="text-muted">
                    {done[f.key] === 'posted' ? (
                      <Badge tone="ok">
                        <Check className="mr-1 inline size-3" /> Posted
                      </Badge>
                    ) : (
                      'Dismissed'
                    )}{' '}
                    {f.finding}
                  </p>
                ) : (
                  <>
                    <Textarea
                      rows={3}
                      aria-label={`Finding ${f.key + 1}`}
                      value={drafts[f.key] ?? ''}
                      onChange={(e) => setDrafts({ ...drafts, [f.key]: e.target.value })}
                    />
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        loading={accept.isPending && accept.variables?.key === f.key}
                        disabled={!drafts[f.key]?.trim()}
                        onClick={() => accept.mutate({ runId: review.runId, body: drafts[f.key]!.trim(), key: f.key })}
                      >
                        Post as comment
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDone({ ...done, [f.key]: 'dismissed' })}>
                        <X /> Dismiss
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Dialog>

      <Dialog
        open={comparing}
        onClose={() => setComparing(false)}
        wide
        title="Compare versions"
        description="A change summary and the points the newer version still leaves open. Proposals only."
        footer={
          <>
            <Button variant="secondary" onClick={() => setComparing(false)}>
              Close
            </Button>
            {comparison && (
              <Button
                onClick={() => accept.mutate({ runId: comparison.runId, body: comparisonBody(comparison) })}
                loading={accept.isPending}
                disabled={summaryPosted}
              >
                {summaryPosted ? 'Posted' : 'Post as comment'}
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Earlier" htmlFor="cmp-from">
              <Select id="cmp-from" value={from} onChange={(e) => setFrom(Number(e.target.value))} className="w-24">
                {versions.map((v) => (
                  <option key={v} value={v}>
                    v{v}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Later" htmlFor="cmp-to">
              <Select id="cmp-to" value={to} onChange={(e) => setTo(Number(e.target.value))} className="w-24">
                {versions.map((v) => (
                  <option key={v} value={v}>
                    v{v}
                  </option>
                ))}
              </Select>
            </Field>
            <Button onClick={() => compare.mutate()} loading={compare.isPending} disabled={from === to}>
              Compare
            </Button>
          </div>
          {comparison && (
            <>
              <p>{comparison.summary}</p>
              {comparison.changes.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">Changes</p>
                  <ul className="list-disc pl-5">
                    {comparison.changes.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {comparison.openPoints.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">Open points</p>
                  <ul className="list-disc pl-5">
                    {comparison.openPoints.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </Dialog>
    </>
  );
}

/** BlackWolf edits a deliverable's review checklist: area and item per line. */
export function ChecklistEditor({
  value,
  saving,
  onSave,
  onCancel,
}: {
  value: Array<{ area: string; item: string }>;
  saving: boolean;
  onSave: (v: Array<{ area: string; item: string }>) => void;
  onCancel: () => void;
}) {
  const [rows, setRows] = useState(value.length ? value : [{ area: '', item: '' }]);
  const valid = rows.every((r) => r.area.trim() && r.item.trim());
  return (
    <div className="space-y-2 px-5 py-3">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <Input
            aria-label={`Area ${i + 1}`}
            className="w-32"
            value={r.area}
            maxLength={80}
            onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, area: e.target.value } : x)))}
          />
          <Input
            aria-label={`Item ${i + 1}`}
            value={r.item}
            maxLength={400}
            onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, item: e.target.value } : x)))}
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Remove item ${i + 1}`}
            onClick={() => setRows(rows.filter((_, j) => j !== i))}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={() => setRows([...rows, { area: '', item: '' }])}>
          <Plus /> Add item
        </Button>
        <Button size="sm" className="ml-auto" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          loading={saving}
          disabled={!valid}
          onClick={() => onSave(rows.map((r) => ({ area: r.area.trim(), item: r.item.trim() })))}
        >
          Save checklist
        </Button>
      </div>
    </div>
  );
}
